import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { Field, Select, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { downloadBlob } from '../../lib/download.js'
import PaymentMethodView from './PaymentMethodView.jsx'

/*
 * Company subscription (settings "Abonnement") — inventory §5.1/§5.2. Plan +
 * current-period usage, internal billing invoices (PDF), cancel/reactivate.
 * Adding a card needs Stripe Elements (SDK) — managed on staging; here we show
 * the current payment-method status.
 */
const STATUS_TONE = { active: 'success', canceled: 'danger', pending_cancellation: 'warning', pending: 'neutral' }
const REASONS = ['too_expensive', 'missing_features', 'switching_competitor', 'no_longer_needed', 'technical_issues', 'other']

export default function SubscriptionTab() {
  const { t } = useTranslation()
  const { currentCompanyId } = useCompany()
  const { confirm, toast, notify } = useFeedback()
  const [data, setData] = useState(null)
  const [pm, setPm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('dashboard') // dashboard | invoices
  const [cancelOpen, setCancelOpen] = useState(false)
  const [reason, setReason] = useState('too_expensive')
  const [feedback, setFeedback] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([api.get('/billing/current'), api.get('/billing/payment-method').catch(() => ({ data: null }))])
      .then(([c, p]) => { setData(c.data); setPm(p.data) }).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load, currentCompanyId])

  const submitCancel = async () => {
    setBusy(true)
    try { await api.post('/billing/cancel', { reason, feedback }); toast(t('subscription.canceled'), 'success'); setCancelOpen(false); load() }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setBusy(false) }
  }
  const reactivate = async () => {
    const ok = await confirm({ tone: 'info', destructive: false, title: t('subscription.confirmReactivate'), confirmLabel: t('subscription.reactivate'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete('/billing/cancel'); toast(t('subscription.reactivated'), 'success'); load() }
    catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) }
  }

  if (loading) return <Card>{t('common.loading')}</Card>
  if (view === 'invoices') return <BillingInvoices t={t} onBack={() => setView('dashboard')} />
  if (view === 'payment') return <PaymentMethodView t={t} onBack={() => { setView('dashboard'); load() }} />

  const { billing, plan, current_period: cp } = data || {}
  const canceled = billing?.status === 'canceled'
  const pendingCancel = billing?.status === 'pending_cancellation' || billing?.cancellation_effective_date

  return (
    <>
      {pendingCancel && (
        <Card style={{ marginBottom: 16, background: 'var(--color-warning-bg)', borderColor: '#f0d9a8', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ color: 'var(--color-warning)' }}><Icon name="info" size={18} /></span>
          <span style={{ color: 'var(--color-warning)', fontSize: 13.5 }}>{t('subscription.pendingCancel', { date: formatDate(billing.cancellation_effective_date) })}</span>
        </Card>
      )}

      {/* Quick actions (handoff/old layout): three cards on top */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 18 }}>
        <ActionCard icon="invoice" title={t('subscription.invoices')} desc={t('subscription.actionInvoicesDesc')} onClick={() => setView('invoices')} />
        {!plan?.is_free && <ActionCard icon="credit-card" title={t('subscription.managePayment')} desc={t('subscription.actionPaymentDesc')} onClick={() => setView('payment')} />}
        {canceled || pendingCancel
          ? <ActionCard icon="check" title={t('subscription.reactivate')} desc={pendingCancel ? t('subscription.actionUndoCancelDesc') : t('subscription.actionReactivateDesc')} tone="var(--color-success)" onClick={reactivate} />
          : <ActionCard icon="x" title={t('subscription.cancel')} desc={t('subscription.actionCancelDesc')} tone="var(--color-danger)" onClick={() => setCancelOpen(true)} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 18, alignItems: 'start' }}>
        {/* Current Plan */}
        <Card padding={0} style={{ overflow: 'hidden', borderTop: '3px solid var(--color-primary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--color-border-row)' }}>
            <SectionTitle style={{ margin: 0 }}>{t('subscription.currentPlan')}</SectionTitle>
            <Badge tone={STATUS_TONE[billing?.status] || 'neutral'}>{t(`subscription.status.${billing?.status}`, billing?.status)}</Badge>
          </div>
          <div style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 24, color: 'var(--color-ink)' }}>{plan?.name}</div>
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>{plan?.description}</p>
              </div>
              {!plan?.is_free && <div style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 20, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>{formatCurrency(plan?.monthly_base_price)}<span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-muted)' }}> / {t('register.month')}</span></div>}
            </div>
            {!plan?.is_free && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-text)', marginBottom: 10 }}>{t('subscription.pricingDetails')}</div>
                <PriceRow icon="send" label={t('subscription.outgoing')} value={t('subscription.perInvoice', { price: formatCurrency(plan?.price_per_outgoing_invoice) })} />
                <PriceRow icon="download" label={t('subscription.incoming')} value={t('subscription.perInvoice', { price: formatCurrency(plan?.price_per_incoming_invoice) })} />
                <PriceRow icon="invoice" label={t('subscription.storageIncluded')} value={t('subscription.nInvoices', { count: plan?.included_stored_invoices })} />
                <PriceRow icon="credit-card" label={t('subscription.additionalTier')} value={formatCurrency(plan?.price_per_100_extra_invoices)} />
              </div>
            )}
          </div>
        </Card>

        {/* Current Usage */}
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--color-border-row)' }}><SectionTitle style={{ margin: 0 }}>{t('subscription.usage')}</SectionTitle></div>
          <div style={{ padding: '18px 22px' }}>
            {cp?.start && <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--color-text)', background: 'var(--color-bg-lilac)', borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>{t('subscription.period')} : {formatDate(cp.start)} – {formatDate(cp.end)}</div>}
            {billing?.next_billing_date && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)', marginBottom: 14 }}><Icon name="calendar" size={15} /> {t('subscription.nextBillingOn', { date: formatDate(billing.next_billing_date) })}</div>}
            <UsageRow icon="send" label={t('subscription.outgoing')} count={cp?.outgoing_invoices_count ?? 0} amount={cp?.estimated_outgoing_amount} />
            <UsageRow icon="download" label={t('subscription.incoming')} count={cp?.incoming_invoices_count ?? 0} amount={cp?.estimated_incoming_amount} />
            <UsageRow icon="invoice" label={t('subscription.stored')} count={`${cp?.total_stored_invoices ?? 0} / ${plan?.included_stored_invoices ?? '∞'}`} />
            <div style={{ borderTop: '1px solid var(--color-border-row)', margin: '12px 0', paddingTop: 12 }}>
              <Row label={t('subscription.base')} value={formatCurrency(cp?.estimated_base_amount)} />
            </div>
            <div style={{ borderTop: '1px solid var(--color-border-row)', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, color: 'var(--color-ink)', fontSize: 14.5 }}>{t('subscription.nextInvoiceEst')}</span>
                <span style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 18, color: 'var(--color-primary)' }}>{formatCurrency(cp?.estimated_total_ttc)} {t('subscription.inclVat')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text-muted)', marginTop: 3 }}><span>{t('subscription.inclVat21')}</span><span>{formatCurrency(cp?.estimated_vat)}</span></div>
            </div>
            {(pm?.payment_methods?.length || 0) > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, padding: '10px 14px', background: 'var(--color-success-bg)', borderRadius: 10 }}>
                <Icon name="credit-card" size={16} />
                <span style={{ flex: 1, fontSize: 13, color: 'var(--color-ink)' }}>{t('subscription.paymentOn', { brand: cap(defaultCard(pm)?.card?.brand), last4: defaultCard(pm)?.card?.last4 })}</span>
                {!plan?.is_free && <button onClick={() => setView('payment')} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>{t('subscription.modify')}</button>}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth={460} closeOnBackdrop={false}>
        <div style={{ padding: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 6 }}>{t('subscription.cancelTitle')}</h3>
          <p style={{ color: 'var(--color-text)', fontSize: 13.5, marginBottom: 16 }}>{t('subscription.cancelWarning')}</p>
          <Field label={t('subscription.reason')}><Select value={reason} onChange={(e) => setReason(e.target.value)}>{REASONS.map((r) => <option key={r} value={r}>{t(`subscription.reasons.${r}`)}</option>)}</Select></Field>
          <Field label={t('subscription.feedback')}><Textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="danger" icon="x" loading={busy} onClick={submitCancel}>{t('subscription.confirmCancel')}</Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

function BillingInvoices({ t, onBack }) {
  const { toast } = useFeedback()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => { api.get('/billing/invoices').then(({ data }) => setRows(data?.invoices || [])).finally(() => setLoading(false)) }, [])
  // Some billing invoices expose a direct pdf_url; otherwise hit the endpoint
  // (which 404s when no invoice is linked → surface the error, don't fail silently).
  const pdf = (inv) => {
    if (inv.pdf_url) { window.open(inv.pdf_url, '_blank', 'noopener'); return }
    downloadBlob(`/billing/invoices/${inv.id}/pdf`, `${inv.invoice_number || inv.id}.pdf`)
      .catch(() => toast(t('subscription.pdfUnavailable'), 'error'))
  }

  return (
    <Card padding={0}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--color-border-row)' }}>
        <Button variant="secondary" onClick={onBack}>{t('common.back')}</Button>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16 }}>{t('subscription.invoices')}</h3>
      </div>
      {loading ? <div style={{ padding: 30, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        : rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('subscription.noInvoices')}</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
              {[t('subscription.period'), t('subscription.description'), t('invoices.columns.amount'), t('admin.companies.status'), ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 2 ? 'right' : 'left', padding: '11px 18px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border-row)' }}>
                  <td style={{ padding: '12px 18px', color: 'var(--color-text)' }}>{formatDate(inv.period_start)} → {formatDate(inv.period_end)}</td>
                  <td style={{ padding: '12px 18px', color: 'var(--color-ink)' }}>{inv.description}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600 }}>{formatCurrency(inv.total_amount_incl_vat ?? inv.total_amount)}</td>
                  <td style={{ padding: '12px 18px' }}><Badge tone={inv.status === 'paid' ? 'success' : inv.status === 'failed' ? 'danger' : 'warning'}>{inv.status}</Badge></td>
                  <td style={{ padding: '12px 18px', textAlign: 'right' }}><Button variant="secondary" icon="download" onClick={() => pdf(inv)}>PDF</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </Card>
  )
}

function SectionTitle({ children, style }) { return <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12, ...style }}>{children}</h3> }

function ActionCard({ icon, title, desc, tone, onClick }) {
  const color = tone || 'var(--color-primary)'
  return (
    <button onClick={onClick} style={{ textAlign: 'center', background: '#fff', border: '1px solid var(--color-border)', borderRadius: 16, boxShadow: 'var(--shadow-card)', padding: '20px 16px', cursor: 'pointer' }}>
      <span style={{ color, display: 'inline-flex', marginBottom: 8 }}><Icon name={icon} size={22} /></span>
      <div style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: tone || 'var(--color-ink)' }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 3 }}>{desc}</div>
    </button>
  )
}

function PriceRow({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0', fontSize: 13.5, color: 'var(--color-text)' }}>
      <span style={{ color: 'var(--color-text-muted)', display: 'inline-flex' }}><Icon name={icon} size={15} /></span>
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{value}</span>
    </div>
  )
}

function UsageRow({ icon, label, count, amount }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', fontSize: 13.5, color: 'var(--color-text)' }}>
      <span style={{ color: 'var(--color-text-muted)', display: 'inline-flex' }}><Icon name={icon} size={15} /></span>
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{ fontWeight: 700, color: 'var(--color-ink)' }}>{count}</span>
      {amount != null && Number(amount) > 0 && <span style={{ color: 'var(--color-primary)', fontWeight: 600, minWidth: 56, textAlign: 'right' }}>{formatCurrency(amount)}</span>}
    </div>
  )
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const defaultCard = (pm) => (pm?.payment_methods || []).find((m) => m.is_default) || (pm?.payment_methods || [])[0]
function Row({ label, value, big, muted }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '3px 0' }}><span style={{ fontSize: big ? 14.5 : 13, color: muted ? 'var(--color-text-muted)' : 'var(--color-text)', fontWeight: big ? 700 : 500 }}>{label}</span><span style={{ fontFamily: 'var(--font-title)', fontSize: big ? 18 : 14, fontWeight: big ? 800 : 600, color: big ? 'var(--color-success)' : 'var(--color-ink)' }}>{value}</span></div>
}
